import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { useConfigDataService } from "../../services/ConfigDataService";
import { useThemeMode } from "../../theme/ThemeContextProvider";

const SyncStatusCards = () => {
  const { fetchAPISyncStatus, syncNow } = useConfigDataService();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedException, setSelectedException] = useState(null);
  const [refreshCountdown, setRefreshCountdown] = useState(181);
  const { mode, toggleTheme } = useThemeMode();
  const [syncingEndpoints, setSyncingEndpoints] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSyncEndpoint, setPendingSyncEndpoint] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await fetchAPISyncStatus();
      setData(result ?? []);
    } catch (err) {
      console.error("Failed to fetch API sync status:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      setRefreshCountdown((prev) => {
        if (prev <= 1) {
          loadData();
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleDialogOpen = (details) => {
    setSelectedException(details || "No exception details available.");
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedException(null);
  };

  const getStatusColor = (overdue, hasException) => {
    if (hasException) return "error";
    if (overdue) return "warning";
    return "success";
  };

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h2">API Sync Status</Typography>
        <Button
          variant={mode === "light" ? "outlined" : "contained"}
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh ({refreshCountdown}s)
        </Button>
      </Grid>
      <Divider sx={{ m: 1 }} />
      <Grid container spacing={2}>
        {loading ? (
          <Grid item xs={12}>
            <CircularProgress />
          </Grid>
        ) : (
          data
            .filter((item) => item.DisplayInUI)
            .map((item) => {
              const hasException =
                item.ExceptionDetails &&
                item.ExceptionDetails.trim() &&
                item.ExceptionDetails !== "exception details = @P1";

              const overdue = item.Overdue === 1;
              const lastSynced = item.LastSynced
                ? dayjs(item.LastSynced).format("DD-MMM-YY hh:mm A")
                : "Never";
              const statusColor = getStatusColor(overdue, hasException);

              return (
                <Grid
                  item
                  size={{ xs: 12, sm: 6, md: 3 }}
                  key={item.API_SyncId}
                >
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      bgcolor: "background.paper",
                    }}
                    elevation={3}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                      }}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                          {item.SyncInProgress ? (
                            <SyncIcon color="primary" />
                          ) : hasException ? (
                            <Tooltip title="View exception details">
                              <IconButton
                                onClick={() =>
                                  handleDialogOpen(item.ExceptionDetails)
                                }
                              >
                                <ErrorOutlineIcon color="error" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <CheckCircleIcon color="success" />
                          )}
                        </Grid>
                        <Grid item>
                          <Typography variant="h6">{item.Endpoint}</Typography>
                        </Grid>
                      </Grid>

                      <Typography variant="body2" mt={1}>
                        <AccessTimeIcon fontSize="small" /> Last Synced:{" "}
                        {lastSynced}
                      </Typography>

                      <Typography variant="body2">
                        Interval: {item.Interval ?? "N/A"} mins
                      </Typography>

                      <Grid container spacing={1} mt={1}>
                        <Grid item>
                          <Chip
                            label={overdue ? "Overdue" : "On Time"}
                            color={statusColor}
                            size="small"
                          />
                        </Grid>
                        {item.SyncInProgress && (
                          <Grid item>
                            <Chip
                              label="Syncing..."
                              color="primary"
                              size="small"
                            />
                          </Grid>
                        )}
                        {hasException && (
                          <Grid item>
                            <Chip
                              label="Exception"
                              color="error"
                              size="small"
                            />
                          </Grid>
                        )}
                      </Grid>

                      <Box mt="auto" pt={2}>
                        <Button
                          variant={mode === "light" ? "outlined" : "contained"}
                          fullWidth
                          size="small"
                          disabled={
                            item.SyncInProgress ||
                            syncingEndpoints[item.Endpoint]
                          }
                          onClick={() => {
                            setPendingSyncEndpoint(item.Endpoint);
                            setConfirmDialogOpen(true);
                          }}
                        >
                          Sync Now
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
        )}
      </Grid>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Exception Details</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>
              {selectedException}
            </pre>
          </DialogContentText>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingSyncEndpoint(null);
        }}
      >
        <DialogTitle>Confirm Sync</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sync <strong>{pendingSyncEndpoint}</strong>
            ?
          </DialogContentText>
        </DialogContent>
        <Box display="flex" justifyContent="flex-end" gap={1} px={3} pb={2}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              setConfirmDialogOpen(false);
              if (!pendingSyncEndpoint) return;

              try {
                setSyncingEndpoints((prev) => ({
                  ...prev,
                  [pendingSyncEndpoint]: true,
                }));
                await syncNow(pendingSyncEndpoint);
                window.location.reload();
              } catch (err) {
                console.error(err);
                alert(`Failed to sync ${pendingSyncEndpoint}`);
              } finally {
                setSyncingEndpoints((prev) => ({
                  ...prev,
                  [pendingSyncEndpoint]: false,
                }));
                setPendingSyncEndpoint(null);
              }
            }}
          >
            Confirm
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SyncStatusCards;
